import { Children, useEffect } from "react";
import { Box, LinearProgress } from "@mui/material";
import { Document, PDFViewer, View } from "@react-pdf/renderer";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";
import { useConfigPageContext } from "src/Contexts/configPagesContext/configPagesContext";
import { useContextReportsProduction } from "src/Contexts/ReportProduction/ReportsProductionContextContext";
import { IReportConfirmationPaymentItem } from "src/Contexts/ReportProduction/ReportsProductionTypes";
import { PDFCabeçalho } from "src/Pages/components/PDF/Cabeçalho";
import { ItemMapToInfoSearch } from "src/Pages/components/PDF/ItemMapToInfoSearch";
import { PageCorePDF } from "src/Pages/components/PDF/PageCorePDF";
import { TableBase } from "src/Pages/components/PDF/TableBase";
import { PDFTableRow } from "src/Pages/components/PDF/TableRow";
import { TableRowContainer } from "src/Pages/components/PDF/TableRowContainer";
import { FormatDateBR, GetDateUSSemValue } from "src/shared/Utils/FormatDateBR";
import { FormatMoney } from "src/shared/Utils/FormatMoney";
import { HandleGetHoursCurrency } from "src/shared/Utils/HandleGetHoursCurrency";

import { HeadReportConfirmationPaymentPDF } from "./HeadReportConfirmationPaymentPDF";

type TPersonType = "CORRETOR" | "SUPERVISOR" | "GERENTE";

const getGroupLabel = (typePerson: TPersonType) => {
  if (typePerson === "CORRETOR") return "Corretores";
  if (typePerson === "SUPERVISOR") return "Supervisores";
  return "Gerentes";
};

const getPercentValue = (
  _typePerson: TPersonType,
  item: IReportConfirmationPaymentItem
) => {
  return item.porcentagem_comissao;
};

const getComissionValue = (
  _typePerson: TPersonType,
  item: IReportConfirmationPaymentItem
) => {
  return item.valor_comissao;
};

const getGroupTotalPaid = (
  typePerson: TPersonType,
  rows: IReportConfirmationPaymentItem[]
) => {
  return rows.reduce((acc, item) => {
    return acc + Number(getComissionValue(typePerson, item) || 0);
  }, 0);
};

export const ReportConfirmationPaymentPDF = () => {
  const { userPerfil } = useAuthContext();
  const { loadingGravar } = useConfigPageContext();
  const { listReportConfirmationPayment, setListReportConfirmationPayment } =
    useContextReportsProduction();

  const data = listReportConfirmationPayment?.data;
  const meta = listReportConfirmationPayment?.meta;
  const totalRows =
    (data?.corretor.length || 0) +
    (data?.supervisor.length || 0) +
    (data?.gerente.length || 0);

  useEffect(() => setListReportConfirmationPayment(undefined), []);

  return (
    <>
      {userPerfil && data && meta && totalRows > 0 && (
        <PDFViewer style={{ height: "84vh" }} width="100%">
          <Document creator="Brokeris" title="Relatório de pagamentos">
            <PageCorePDF orientation="landscape" padding="16px 8px 30px 8px">
              <PDFCabeçalho
                dateCurrency={FormatDateBR(GetDateUSSemValue())}
                empresa=""
                endDate={FormatDateBR(meta.dataPayments.endDate)}
                hoursCurrency={HandleGetHoursCurrency()}
                nomePdf="Relatório de pagamentos"
                startDate={FormatDateBR(meta.dataPayments.startDate)}
              />

              <View
                fixed
                style={{
                  borderBottom: "1px solid black",
                  marginBottom: "14px",
                }}
              >
                <ItemMapToInfoSearch
                  itens={[meta.typeReport === "ALL" ? "Todos" : "Pessoa única"]}
                  title="Tipo de relatório"
                />
                <ItemMapToInfoSearch
                  itens={[meta.funcaoProdutor || "Todos"]}
                  title="Tipo de pessoa"
                />
              </View>

              <TableBase margin="0">
                <HeadReportConfirmationPaymentPDF />

                {Children.toArray(
                  [
                    { type: "CORRETOR" as TPersonType, rows: data.corretor, },
                    { type: "SUPERVISOR" as TPersonType, rows: data.supervisor, },
                    { type: "GERENTE" as TPersonType, rows: data.gerente, },
                  ].map(({ type, rows }) =>
                    <>
                      {rows.length > 0 && (
                        <TableRowContainer
                          key={`LIST-${type}`}
                          backgroundColor="#eeeeee"
                        >
                          <PDFTableRow
                            fontSize={10}
                            paddingLeft="8px"
                            title={`Pagamentos de ${getGroupLabel(type)}`}
                            width="100%"
                          />

                        </TableRowContainer>
                      )}
                      {rows.map((item) => (
                        <TableRowContainer key={`${type}-${item.id}`}>
                          <PDFTableRow
                            paddingLeft="8px"
                            title={`${item.id_produtor_pagamento} - ${item.nome_produtor_pagamento}`}
                            width="15%"
                          />
                          <PDFTableRow
                            title={item.nome_operadora}
                            width="10%"
                          />
                          <PDFTableRow
                            textAlign="center"
                            title={item.cod_proposta}
                            width="10%"
                          />
                          <PDFTableRow
                            textAlign="center"
                            title={item.numero_parcela === 0 ? "Adesão" : item.numero_parcela}
                            width="6%"
                          />
                          <PDFTableRow
                            textAlign="right"
                            title={item.data_recibo ? FormatDateBR(item.data_recibo) : ""}
                            width="9%"
                          />
                          <PDFTableRow
                            textAlign="right"
                            title={FormatDateBR(item.data_vencimento_parcela)}
                            width="9%"
                          />
                          <PDFTableRow
                            textAlign="center"
                            title={item.vidas}
                            width="5%"
                          />
                          <PDFTableRow
                            paddingLeft="6px"
                            textAlign="right"
                            title={FormatMoney(String(item.valor_recebido))}
                            width="9%"
                          />
                          <PDFTableRow
                            textAlign="right"
                            title={`${String(getPercentValue(type, item)).replace(".", ",")} %`}
                            width="10%"
                          />
                          <PDFTableRow
                            textAlign="right"
                            title={FormatMoney(String(getComissionValue(type, item)))}
                            width="17%"
                            paddingRight="8px"
                          />
                        </TableRowContainer>
                      ))}

                      {rows.length > 0 && (
                        <TableRowContainer
                          key={`total-${type}`}
                          backgroundColor="#eeeeee"
                        >
                          <PDFTableRow
                            fontSize={10}
                            paddingLeft="8px"
                            title={`Total ${getGroupLabel(type)} - ${rows.length}`}
                            width="83%"
                          />
                          <PDFTableRow
                            fontSize={10}
                            paddingRight="8px"
                            textAlign="right"
                            title={FormatMoney(getGroupTotalPaid(type, rows).toFixed(2))}
                            width="17%"
                          />
                        </TableRowContainer>
                      )}
                    </>
                  )
                )}

                <TableRowContainer backgroundColor="#eeeeee">
                  <PDFTableRow
                    fontSize={11}
                    paddingLeft="8px"
                    title={`Total de pagamentos: ${meta.total}`}
                    width="83%"
                  />
                  <PDFTableRow
                    fontSize={11}
                    paddingRight="8px"
                    textAlign="right"
                    title={FormatMoney(meta.resume.valorTotalPagoGeral.toFixed(2))}
                    width="17%"
                  />
                </TableRowContainer>
              </TableBase>
            </PageCorePDF>
          </Document>
        </PDFViewer>
      )}

      {loadingGravar && (
        <Box style={{ width: "100%" }}>
          <LinearProgress color="inherit" />
        </Box>
      )}
    </>
  );
};
